import { useContext, useState, createContext, type PropsWithChildren } from "react";

import { Booking, IBookingScanOptionsProperties } from "../logic/api";
import { SessionContext } from "../utility/useSession";
import { Optional } from "../types/utility";
import BarcodeScanner from "../components/thirdParty/react-barcode-reader";
import { ReleaseFlag, useReleaseManagement } from "../utility/feature-management";

interface IBarcodeScannerContext {
  scan: string;
  resetScan: () => void;
  matchBooking: (booking: Booking) => boolean;
}

const BarcodeScannerContext = createContext<Optional<IBarcodeScannerContext>>(undefined);

export const useBarcodeScanner = (): IBarcodeScannerContext => {
  const context = useContext(BarcodeScannerContext);
  if (context === undefined) {
    throw new Error('useBarcodeScanner must be used within a BarcodeScannerProvider');
  }

  return context;
};

export const BarcodeScannerProvider = ({ children }: PropsWithChildren) => {
  const session = useContext(SessionContext);
  const [value, setValue] = useState("");
  const isScanByExternalReferenceReleased = useReleaseManagement(ReleaseFlag.releaseAdditionalExternalReferences);

  const opts =
    session.current.site?.preferences.bookingScanOptions?.properties ||
    ({} as IBookingScanOptionsProperties);
  opts.enableRef = opts.enableRef ?? true;
  opts.enableUuid = opts.enableUuid ?? true;
  opts.enableBookingId = opts.enableBookingId ?? true;
  opts.enableImportedId = opts.enableImportedId ?? true;

  if (isScanByExternalReferenceReleased) {
    opts.enableExternalReferences = opts.enableExternalReferences ?? true;
  } else {
    opts.enableExternalReferences = false;
  }

  function matchBooking(b: Booking) {
    if (opts.enableUuid) {
      if (value.length === 36) {
        return b.uuid === value;
      }
    }

    if (opts.enableRef) {
      return b.ref === value;
    }

    if (opts.enableBookingId) {
      const scanAsInt = Number(value);
      if (isNaN(scanAsInt)) {
        return false;
      }

      return b.id === scanAsInt;
    }

    if (opts.enableImportedId) {
      const scanAsInt = Number(value);
      if (isNaN(scanAsInt)) {
        return false;
      }

      return b.importedId === scanAsInt;
    }

    if (opts.enableExternalReferences && b.externalRefs) {
      return Boolean(b.externalRefs.find(externalRef => externalRef.ref === value));
    }

    return false;
  }

  function resetScan() {
    setValue("");
  }

  return (
    <BarcodeScannerContext.Provider value={{
      scan: value,
      matchBooking,
      resetScan,
    }}>
      {children}
      <BarcodeScanner
        onError={(e) => {
          console.error(e);
        }}
        onScan={(scan) => {
          if (scan.trim()) {
            setValue(scan.trim());
          }
        }}
        minLength={1}
      />
    </BarcodeScannerContext.Provider>
  );
};
